import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../utils/Input';
import toast from 'react-hot-toast';
import Button from '../utils/Button';
import { useApiMutation } from '../hooks/useMutation';

interface FormData {
  organizationName: string;
  organizationType: string;
  websiteUrl: string;
  primaryContactName: string;
  email: string;
  phone: string;
  statesOfOperation: string;
  intendedUseCases: string[];
}

interface FormErrors {
  organizationName?: string;
  organizationType?: string;
  websiteUrl?: string;
  primaryContactName?: string;
  email?: string;
  phone?: string;
  statesOfOperation?: string;
  intendedUseCases?: string;
}

const organizationTypes = [
  'NONPROFIT_501C3',
  'ADVOCACY_501C4',
  'PAC',
  'CAMPAIGN_COMMITTEE',
  'FOUNDATION',
  'UNIVERSITY',
  'CORPORATE_CSR',
  'POLITICAL_PARTY',
  'OTHER'
];

const intendedUseOptions = [
  'DIGITAL_VOTER_REGISTRATION',
  'FIELD_REGISTRATION',
  'STATUS_CHECK',
  'POLLING_LOOKUP',
  'VOTE_BY_MAIL',
  'PLEDGE_TO_VOTE',
  'DATA_ATTRIBUTION',
  'MULTI_STATE',
  'API_INTEGRATION'
];

const PartnerWithUs: React.FC = () => {
  const navigate = useNavigate();
  
  const partnerContactMutation = useApiMutation({
    onSuccess: () => {
      toast.success('Partner application submitted successfully!');
      navigate('/');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to submit application. Please try again.';
      toast.error(errorMessage);
    }
  });

  const [formData, setFormData] = useState<FormData>({
    organizationName: '',
    organizationType: '',
    websiteUrl: '',
    primaryContactName: '',
    email: '',
    phone: '',
    statesOfOperation: '',
    intendedUseCases: []
  });

  const [errors, setErrors] = useState<FormErrors>({});

  const validateEmail = (email: string): string | null => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return !emailRegex.test(email) ? 'Please enter a valid email address' : null;
  };

  const validatePhone = (phone: string): string | null => {
    const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    return !phoneRegex.test(phone) ? 'Please enter a valid phone number' : null;
  };

  const validateUrl = (url: string): string | null => {
    try {
      new URL(url);
      return null;
    } catch {
      return 'Please enter a valid website URL';
    }
  };

  const handleInputChange = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleUseCaseToggle = (useCase: string) => {
    setFormData(prev => ({
      ...prev,
      intendedUseCases: prev.intendedUseCases.includes(useCase)
        ? prev.intendedUseCases.filter(uc => uc !== useCase)
        : [...prev.intendedUseCases, useCase]
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.organizationName.trim()) {
      newErrors.organizationName = 'Organization name is required';
    }

    if (!formData.organizationType) {
      newErrors.organizationType = 'Please select an organization type';
    }

    if (!formData.websiteUrl.trim()) {
      newErrors.websiteUrl = 'Website URL is required';
    } else {
      const urlError = validateUrl(formData.websiteUrl);
      if (urlError) newErrors.websiteUrl = urlError;
    }

    if (!formData.primaryContactName.trim()) {
      newErrors.primaryContactName = 'Contact name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Work email is required';
    } else {
      const emailError = validateEmail(formData.email);
      if (emailError) newErrors.email = emailError;
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else {
      const phoneError = validatePhone(formData.phone);
      if (phoneError) newErrors.phone = phoneError;
    }

    if (!formData.statesOfOperation.trim()) {
      newErrors.statesOfOperation = 'States of operation is required';
    }

    if (formData.intendedUseCases.length === 0) {
      newErrors.intendedUseCases = 'Please select at least one intended use case';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    const apiData = {
      organizationName: formData.organizationName,
      organizationType: formData.organizationType,
      websiteUrl: formData.websiteUrl,
      primaryContactName: formData.primaryContactName,
      email: formData.email,
      phone: formData.phone,
      statesOfOperation: formData.statesOfOperation.split(',').map(state => state.trim()).filter(state => state),
      intendedUseCases: formData.intendedUseCases
    };

    partnerContactMutation.mutate({
      method: 'POST',
      url: '/admin/partner-contact',
      data: apiData
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-slate-800 px-8 py-7 rounded-2xl text-center">
            <h1 className="text-3xl lg:text-4xl font-bold text-white mb-4">Partner With Us</h1>
            <p className="text-blue-100 max-w-2xl mx-auto">
              Join us in our mission to empower voters across the nation. Let's work together to make democracy more accessible.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-8 py-8 space-y-8">
            {/* Organization Information */}
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold text-gray-900 border-b pb-2">Organization Information</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Organization Name"
                  name="organizationName"
                  value={formData.organizationName}
                  onChange={handleInputChange('organizationName')}
                  error={errors.organizationName}
                  placeholder="Enter your organization name"
                  size="large"
                  required
                  color='white'
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization Type
                  </label>
                  <select
                    name="organizationType"
                    value={formData.organizationType}
                    onChange={(e) => handleInputChange('organizationType')(e as any)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select organization type</option>
                    {organizationTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  {errors.organizationType && (
                    <p className="mt-1 text-xs text-red-500">{errors.organizationType}</p>
                  )}
                </div>
              </div>

              <Input
                label="Website URL"
                name="websiteUrl"
                value={formData.websiteUrl}
                onChange={handleInputChange('websiteUrl')}
                error={errors.websiteUrl}
                placeholder="https://example.com"
                size="large"
                onValidate={validateUrl}
                color='white'
                required
              />
            </div>

            <div className="space-y-6">
              <h2 className="text-2xl font-semibold text-gray-900 border-b pb-2">Contact Information</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Primary Contact Name"
                  name="primaryContactName"
                  value={formData.primaryContactName}
                  onChange={handleInputChange('primaryContactName')}
                  error={errors.primaryContactName}
                  placeholder="John Doe"
                  size="large"
                  required
                  color='white'

                />

                <Input
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  error={errors.email}
                  placeholder="john@example.com"
                  size="large"
                  onValidate={validateEmail}
                  required
                  color='white'

                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange('phone')}
                  error={errors.phone}
                  placeholder="+1 (555) 123-4567"
                  size="large"
                  onValidate={validatePhone}
                  required
                  color='white'

                />

                <Input
                  label="State(s) of Operation"
                  name="statesOfOperation"
                  value={formData.statesOfOperation}
                  onChange={handleInputChange('statesOfOperation')}
                  error={errors.statesOfOperation}
                  placeholder="California, Texas, New York"
                  size="large"
                  required
                  color='white'

                />
              </div>
            </div>

            <div className="space-y-6">
              <h2 className="text-2xl font-semibold text-gray-900 border-b pb-2">Intended Use Cases</h2>

              <div>
                <p className="text-sm text-gray-600 mb-4">Select all that apply:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {intendedUseOptions.map(useCase => (
                    <label
                      key={useCase}
                      className="flex items-start space-x-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={formData.intendedUseCases.includes(useCase)}
                        onChange={() => handleUseCaseToggle(useCase)}
                        className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{useCase}</span>
                    </label>
                  ))}
                </div>
                {errors.intendedUseCases && (
                  <p className="mt-2 text-xs text-red-500">{errors.intendedUseCases}</p>
                )}
              </div>
            </div>

            <div className="flex justify-center pt-6">
              <Button
                type="submit"
                disabled={partnerContactMutation.isPending}
                label={partnerContactMutation.isPending ? 'Submitting...' : 'Submit Application'}
                variant='primary'
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PartnerWithUs;
